import { CalendarCheck, Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { formatarDataHora } from '@/nucleo/formato/datas';
import { Button } from '@/ui/base/button';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { MobileCardList, type ItemDoCard } from '@/ui/MobileCardList';
import { PageHeader } from '@/ui/PageHeader';
import { PaginacaoCompacta } from '@/ui/PaginacaoCompacta';
import { StatusBadge } from '@/ui/StatusBadge';

import { AvisoSementeDoSorteio } from './ciclo-inscricao/AvisoSementeDoSorteio';
import { type CicloResumo, situacaoDoCiclo } from './ciclo-inscricao/contrato';
import {
  mensagemDoErro,
  TAMANHO_DA_PAGINA,
  useCriarCiclo,
  useListaDeCiclos,
} from './ciclo-inscricao/dados';
import { DetalheDoCiclo } from './ciclo-inscricao/DetalheDoCiclo';
import { FormularioDoCiclo } from './ciclo-inscricao/FormularioDoCiclo';

/**
 * `/admin/parametros/ciclos-inscricao` — período de inscrição do
 * credenciamento (J-A, RN-CP-01 a 05). Lista, detalhe (`?ciclo=<id>`) e
 * formulário, com dado real da API (`bff/v1/admin/ciclos-inscricao`).
 *
 * O detalhe mora na mesma rota, por parâmetro de busca: o mapa de telas de
 * Parametrização é por caminho exato, e o ciclo não precisa de endereço próprio
 * para ser compartilhado — o parâmetro já basta.
 */
export function CicloInscricaoPage() {
  const [busca, setBusca] = useSearchParams();
  const cicloId = busca.get('ciclo');

  if (cicloId) {
    return <DetalheDoCiclo key={cicloId} id={cicloId} />;
  }
  return (
    <ListaDeCiclos
      aoAbrir={(id) =>
        setBusca((b) => {
          b.set('ciclo', id);
          return b;
        })
      }
      pagina={Math.max(1, Number(busca.get('pagina')) || 1)}
      aoMudarPagina={(p) =>
        setBusca((b) => {
          b.set('pagina', String(p));
          return b;
        })
      }
    />
  );
}

function ListaDeCiclos({
  aoAbrir,
  pagina,
  aoMudarPagina,
}: {
  aoAbrir: (id: string) => void;
  pagina: number;
  aoMudarPagina: (p: number) => void;
}) {
  const consulta = useListaDeCiclos(pagina);
  const criar = useCriarCiclo();
  const [formulario, setFormulario] = useState(false);
  // Uma chave por abertura do formulário: reenvio do mesmo pedido não cria dois ciclos.
  const chave = useRef(crypto.randomUUID());

  function abrirFormulario() {
    chave.current = crypto.randomUUID();
    criar.reset();
    setFormulario(true);
  }

  const colunas: DataTableColumn<CicloResumo>[] = [
    {
      header: 'Ciclo',
      cell: (c) => <span className="font-medium">{c.nome}</span>,
    },
    {
      header: 'Situação',
      cell: (c) => {
        const s = situacaoDoCiclo(c);
        return <StatusBadge status={s.variante} label={s.rotulo} />;
      },
    },
    {
      header: 'Período',
      cell: (c) => (
        <span className="text-sm">
          {formatarDataHora(c.inicioEm)} a {formatarDataHora(c.fimEm)}
        </span>
      ),
    },
    { header: 'Vagas', cell: (c) => <span className="font-mono">{c.totalVagas}</span> },
    { header: 'Inscrições', cell: (c) => <span className="font-mono">{c.totalInscricoes}</span> },
  ];

  const ciclos = consulta.data?.itens ?? [];
  const cartoes: ItemDoCard[] = ciclos.map((c) => {
    const s = situacaoDoCiclo(c);
    return {
      id: c.id,
      title: c.nome,
      badges: <StatusBadge status={s.variante} label={s.rotulo} />,
      fields: [
        {
          label: 'Período',
          value: `${formatarDataHora(c.inicioEm)} a ${formatarDataHora(c.fimEm)}`,
          full: true,
        },
        { label: 'Vagas', value: c.totalVagas },
        { label: 'Inscrições', value: c.totalInscricoes },
      ],
      onClick: () => aoAbrir(c.id),
    };
  });
  const vazio = {
    emptyIcon: CalendarCheck,
    emptyTitle: 'Nenhum ciclo de inscrição ainda',
    emptyDescription:
      'Crie o primeiro ciclo: ele nasce como rascunho e só abre com o edital e as vagas informados.',
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ciclos de Inscrição"
        description="Período de inscrição do credenciamento: datas, edital, DREs, modalidades e vagas."
      >
        {consulta.data && (
          <Button onClick={abrirFormulario}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Novo ciclo
          </Button>
        )}
      </PageHeader>

      <AvisoSementeDoSorteio />

      {consulta.isError ? (
        <ErrorState
          variant="load-failed"
          description={mensagemDoErro(
            consulta.error,
            'Não foi possível carregar os ciclos agora. Tente de novo em instantes.',
          )}
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      ) : (
        <>
          <div className="sm:hidden">
            <MobileCardList items={cartoes} isLoading={consulta.isLoading} {...vazio} />
          </div>
          <div className="hidden min-w-0 sm:block">
            <DataTable
              ariaLabel="Ciclos de inscrição"
              columns={colunas}
              data={ciclos}
              isLoading={consulta.isLoading}
              onRowClick={(c) => aoAbrir(c.id)}
              {...vazio}
              emptyActionLabel={consulta.data ? 'Novo ciclo' : undefined}
              onEmptyAction={consulta.data ? abrirFormulario : undefined}
            />
          </div>
          {/* O tamanho da página é fixo no servidor: sem o seletor "Exibir" do DataTable. */}
          <PaginacaoCompacta
            pagina={pagina}
            tamanho={TAMANHO_DA_PAGINA}
            total={consulta.data?.total ?? 0}
            aoMudar={aoMudarPagina}
          />
        </>
      )}

      {formulario && consulta.data && (
        <FormularioDoCiclo
          aberto
          aoFechar={() => setFormulario(false)}
          dres={consulta.data.dres}
          enviando={criar.isPending}
          erro={criar.error ? mensagemDoErro(criar.error, 'Não foi possível criar o ciclo.') : null}
          aoEnviar={(entrada) =>
            criar.mutate(
              { ...entrada, chaveIdempotencia: `ciclo-${chave.current}` },
              {
                onSuccess: (d) => {
                  toast.success('Rascunho criado.');
                  setFormulario(false);
                  aoAbrir(d.ciclo.id);
                },
              },
            )
          }
        />
      )}
    </div>
  );
}
